import './TextAreaInput.css';

import {
  TextAreaControl,
  TextAreaInputValue,
} from '../../../../../types/types';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import TextArea from '../TextArea/TextArea';
import JsonEditor from '../JsonEditor/JsonEditor';
import { validateTextInput } from '../../../../../utils/managerUtils';
import { reformatJsonString } from './utils/reformatJsonString';
import { toJsonEditorValue } from '../../utils/toJsonEditorValue';
import type { GridInputBaseProps } from '../../types';
import { useMemo } from 'react';
import { parseStringValue } from '../../utils/parseStringValue';

type TextAreaInputProps = Omit<
  GridInputBaseProps<TextAreaInputValue>,
  'name' | 'isColumnFilter'
> & {
  control?: TextAreaControl;
};

const TextAreaInput = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  error,
  setError,
  disabled,
  control,
}: TextAreaInputProps) => {
  const { t } = useTranslate();
  const isJson = control?.rendering === 'json';
  const rows = control?.height || 5;

  const sanitizedValue = useMemo(() => {
    if (isJson || (typeof value === 'object' && value !== null)) {
      return toJsonEditorValue(value);
    }

    return parseStringValue(value);
  }, [value]);

  const onInputChange = (value: string) => {
    onChange(value);
    onTouch?.(value);
    setError(null);
  };

  const onInputBlur = () => {
    if (!sanitizedValue || typeof sanitizedValue !== 'string') {
      onFocusOut?.();
      return;
    }
    const textError = validateTextInput(sanitizedValue, control, t);
    if (textError) {
      setError(textError);
      return;
    }
    if (isJson) {
      const result = reformatJsonString(sanitizedValue);
      if (!result.ok) {
        setError([t('errors.invalidJson')]);
        return;
      }
      if (result.changed || typeof value !== 'string') {
        onChange(result.formatted);
      }
    }
    onFocusOut?.();
  };

  if (isJson) {
    return (
      <JsonEditor
        value={sanitizedValue || ''}
        onChange={onInputChange}
        onBlur={onInputBlur}
        onFocus={onFocusIn}
        disabled={disabled}
        error={error}
        rows={rows}
      />
    );
  }

  return (
    <TextArea
      className="text-area-input"
      value={sanitizedValue || ''}
      onChange={onInputChange}
      onBlur={onInputBlur}
      onFocus={onFocusIn}
      disabled={disabled}
      error={error}
      rows={rows}
    />
  );
};

export default TextAreaInput;
